import { useEffect } from "react";
import { API_ENDPOINT, APP_ID } from "../utils/constants";
import { getDbHeader } from "../utils/util";

function GtagLoader() {
  useEffect(() => {
    async function initializeGtag() {
      try {
        // 1. Fetch gtag configuration from API
        const response = await fetch(`${API_ENDPOINT}/event/get-gtag-config`, {
          ...getDbHeader(APP_ID),
          method: "GET",
        });

        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        const data = await response.json();

        // 2. Extract Data
        const trackingId = data?.gtag?.trackingId;
        const gtmId = data?.gtag?.gtmId;
        const googleSiteVerification = data?.gtag?.googleSiteVerification;

        // 3. Inject Google Site Verification Meta Tag
        if (
          googleSiteVerification &&
          typeof googleSiteVerification === "string" &&
          googleSiteVerification.trim() !== ""
        ) {
          let metaTag = document.querySelector(
            'meta[name="google-site-verification"]'
          );

          if (!metaTag) {
            metaTag = document.createElement("meta");
            metaTag.name = "google-site-verification";
            document.head.appendChild(metaTag);
          }

          metaTag.content = googleSiteVerification.trim();
        }

        // 4. Validate Tracking IDs
        const hasTrackingId =
          trackingId &&
          typeof trackingId === "string" &&
          trackingId.trim() !== "";
        const hasGtmId =
          gtmId && typeof gtmId === "string" && gtmId.trim() !== "";

        if (!hasTrackingId && !hasGtmId) {
          throw new Error(
            "Both GTAG Tracking ID and GTM ID are missing or invalid."
          );
        }

        // 5. Initialize data layer (Required for both GTAG and GTM)
        window.dataLayer = window.dataLayer || [];

        // 6. Define gtag function if it doesn't exist
        window.gtag =
          window.gtag ||
          function () {
            window.dataLayer.push(arguments);
          };

        // 7. Conditionally load the gtag.js script and config
        if (hasTrackingId) {
          const gtagScript = document.createElement("script");
          gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
          gtagScript.async = true;

          gtagScript.onload = () => {
            console.log(`GTag loaded successfully for ID: ${trackingId}`);
          };

          gtagScript.onerror = () => {
            console.error("Failed to load gtag.js script");
          };

          document.head.appendChild(gtagScript);

          // Initialize gtag with configuration
          window.gtag("js", new Date());
          window.gtag("config", trackingId, {
            ...(data.gtag.additionalConfig || {}),
            send_page_view: false, // Handled by GtagRouterTracker
          });
        }

        // 8. Conditionally add GTM script
        if (hasGtmId) {
          // GTM script
          const gtmScript = document.createElement("script");
          gtmScript.async = true;
          gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;

          gtmScript.onload = () => {
            console.log(`GTM loaded successfully for ID: ${gtmId}`);
          };

          gtmScript.onerror = () => {
            console.error("Failed to load GTM script");
          };

          document.head.appendChild(gtmScript);

          // GTM noscript (for browsers with JavaScript disabled)
          const gtmNoscript = document.createElement("noscript");
          const iframe = document.createElement("iframe");
          iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
          iframe.height = "0";
          iframe.width = "0";
          iframe.style.display = "none";
          iframe.style.visibility = "hidden";

          gtmNoscript.appendChild(iframe);
          document.body.appendChild(gtmNoscript);
        }
      } catch (error) {
        console.error("GTag/GTM initialization error:", error.message);
      }
    }

    initializeGtag();

    return () => {
      // Cleanup logic if needed
    };
  }, []);

  return null;
}

export default GtagLoader;
